import { Injectable } from '@angular/core';
import { ColDef } from 'ag-grid-community';
import {
  AccessPermissionService,
  DELETE_RECORD_LABEL,
  DISTANCE_KM_LABEL,
  EOLEntrySource,
  StorageTypeEnum,
  dblclickTooltipDef,
  defaultColumn,
  grid_widths_map,
  keysToExclude,
  minNoOfTripsErr,
  minPercentReuseEOLerr,
  noOfTripsErr,
  percentReuseEOLerr,
} from '@piqet-angular/shared';
import { right } from '@popperjs/core';
import { ProjectsFacade, commonColumns, decrypt } from '@piqet-angular/data';
import { cloneDeep } from 'lodash';
import { NumericCellEditor } from 'libs/ui/src/lib/numeri-cell-editor/numeric-cell-editor.component';
import { ActivatedRoute } from '@angular/router';
import { Subject, take } from 'rxjs';
import { SwalService } from 'libs/shared/src/lib/service/swal.service';
import { SharedService } from 'libs/data/src/lib/services/shared/shared.service';
import { ProjectsService } from 'libs/data/src/lib/services/projects/projects.service';
import { ToastrService } from 'ngx-toastr';

@Injectable({
  providedIn: 'root',
})
export class ProjectPackEOLDataGridService {
  editPreRetailSuccess = new Subject<any>();
  editPreRetailSuccessData$ = this.editPreRetailSuccess.asObservable();

  editPostRetailSuccess = new Subject<any>();
  editPostRetailSuccessData$ = this.editPostRetailSuccess.asObservable();

  deletePackageEOL = new Subject<any>();
  deletePackageEOLData$ = this.deletePackageEOL.asObservable();

  // edit new eol pre retail
  editProjectPreRetailSub = new Subject<[any, any]>();
  editProjectPreRetailSubData$ = this.editProjectPreRetailSub.asObservable();

  editProjectPreRetailManualDistanceSub = new Subject<any>();
  editProjectPreRetailManualDistanceSubData$ =
    this.editProjectPreRetailManualDistanceSub.asObservable();

  checkDeleteRowPreRetail = new Subject<[any, any, boolean]>();
  checkDeleteRowPreRetailData$ = this.checkDeleteRowPreRetail.asObservable();

  saveProjectManualDistanceEOL = new Subject<[any, any]>();
  saveProjectManualDistanceEOLData$ = this.saveProjectManualDistanceEOL.asObservable();

  clearInputFieldProjectManualDistanceEOL = new Subject<any>();
  clearInputFieldProjectManualDistanceEOLData$ =
    this.clearInputFieldProjectManualDistanceEOL.asObservable();

  inlineEditTrips = new Subject<any>();
  inlineEditTripsObs$ = this.inlineEditTrips.asObservable();

  reuseComponentsItem = new Subject<any>();
  reuseComponentsItemSub$ = this.reuseComponentsItem.asObservable();

  updateNotification = new Subject<any>();
  updateNotificationSub$ = this.updateNotification.asObservable();

  reuseDeleteComponentsItem = new Subject<any>();
  reuseDeleteComponentsItemSub$ = this.reuseDeleteComponentsItem.asObservable();

  editReuseProcessItem = new Subject<any>();
  editReuseProcessItemSub$ = this.editReuseProcessItem.asObservable();

  saveReuseProcessItem = new Subject<any>();
  saveReuseProcessItemSub$ = this.saveReuseProcessItem.asObservable();

  allTransportLocationRecords: any;
  projectPackEOLPayload: any;
  event: any;
  packageScenarioId: any;
  scenarioId: any;
  StorageTypeEnum = StorageTypeEnum;
  activatedRoute: any;

  constructor(
    private projectsFacade: ProjectsFacade,
    public route: ActivatedRoute,
    private swalService: SwalService,
    private sharedService: SharedService,
    private projectsService: ProjectsService,
    private toaster: ToastrService,
    private accessService: AccessPermissionService
  ) {
    this.activatedRoute = this.route.snapshot.routeConfig?.path;
    this.route.queryParams.subscribe((values: any) => {
      let value: any = decrypt(values.param ? values.param : '');
      value = value && !value.includes('undefined') ? JSON.parse(value) : '';
      this.packageScenarioId = value['packageScenarioId'];
    });
  }

  // For EOL INLINE GRID
  getProjectRetailEOLDataColumn() {
    let enableEditing: any;
    let resetData: any;

    const isRowTotalValid = (params: any) => {
      let total = 0;
      commonColumns.forEach((col: any) => {
        const value = params.data[col.field];
        total += value || 0;
      });
      return total >= 97 && total <= 103;
    };

    const cellRendererTextView = (params: any) => {
      let value = params.value ? params.value : 0;
      let title = '';

      // Determine the title based on colId
      switch (params.colDef.colId) {
        case 'MaterialName':
          title = params.data.materialName;
          break;
        case 'ComponentName':
          title = params.data.componentName;
          break;
        default:
          title = value;
      }

      return `<span class="text-view" title="${title}">${value}</span>`;
    };
    const valueSetter = (field: string) => (params: any) => {
      const newValInt = params.newValue;
      const valueChanged = !resetData ? params.data[field] !== newValInt : false;
      params.data = { ...params.data };
      if (valueChanged) {
        params.data[field] =
          typeof params.oldValue === 'number' ? parseFloat(newValInt) : newValInt;
      }
      params.data['enableEditing'] = enableEditing;
      return valueChanged;
    };
    const commonSettings = {
      minWidth: 200,
      editable: true,
      cellEditor: NumericCellEditor,
      cellStyle: (params: any) => {
        if (!isRowTotalValid(params)) {
          return { 'background-color': '#ffeeee' }; // Light red background
        }
        return null;
      },
      cellRenderer: cellRendererTextView,
    };

    const columnsConfig = [
      {
        colId: 'MaterialName',
        headerName: 'Material',
        field: 'materialName',
        minWidth: 300,
        cellRenderer: cellRendererTextView,
        cellStyle: (params: any) => {
          if (!isRowTotalValid(params)) {
            return { 'background-color': '#ffeeee' }; // Light red background
          }
          return null;
        },
      },
      {
        colId: 'ComponentName',
        headerName: 'Component',
        field: 'componentName',
        minWidth: 300,
        cellRenderer: cellRendererTextView,
        cellStyle: (params: any) => {
          if (!isRowTotalValid(params)) {
            return { 'background-color': '#ffeeee' }; // Light red background
          }
          return null;
        },
      },
      ...commonColumns.map((col: any) => ({
        ...col,
        valueSetter: valueSetter(col.field),
        ...commonSettings,
      })),
    ];
    const actionColumn = {
      ...defaultColumn,
      colId: 'Actions',
      headerName: 'Actions',
      field: 'Actions',
      sortable: false,
      suppressColumnsToolPanel: true,
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'inlineActionButtonComponent',
      maxWidth: grid_widths_map.sm_column_xs,
      cellRendererParams: {
        showInlineEdit: true,
        inlineEditData: (data: any) => {
          enableEditing = data.data.enableEditing;
          resetData = data.data.resetData;
          if (!resetData && data.data?.isInvalid) {
            const dataStr: any = data.columnApi.columnModel.columnDefs[
              data.columnApi.columnModel.columnDefs.length - 1
            ].cellRendererParams.gridType
              ? localStorage.getItem('consumerEOL')
              : localStorage.getItem('retailEOL');
            const dataObj = JSON.parse(dataStr);
          } else if (!resetData && !data.data?.isInvalid) {
            const dataStr: any = data.columnApi.columnModel.columnDefs[
              data.columnApi.columnModel.columnDefs.length - 1
            ].cellRendererParams.gridType
              ? localStorage.getItem('consumerEOL')
              : localStorage.getItem('retailEOL');
            const dataObj = JSON.parse(dataStr);
            data.columnApi.columnModel.columnDefs[data.columnApi.columnModel.columnDefs.length - 1]
              .cellRendererParams.gridType
              ? this.projectsFacade.updatePackageEOLConsumer(dataObj, 'edit', null, '', '')
              : this.projectsFacade.updatePackageEOLRetail(dataObj, 'edit', null, '', '');
          }
        },
      },
      lockVisible: true,
      lockPosition: 'right',
      resizable: false,
      pinned: 'right',
    };

    const colDef = columnsConfig.map((col: any) => ({
      ...defaultColumn,
      ...col,
      cellClass: col.cellClass || 'mainGridEdit',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      resizable: false,
      sortable: false,
      cellEditorParams: col.cellEditor ? { from: 'packageEOL' } : undefined,
    }));

    this.accessPermission() && colDef.push(actionColumn);

    return colDef;
  }

  savePackageEOLData(colType?: any) {
    const eolData: any =
      colType == 'isConsumer'
        ? localStorage.getItem('consumerEOL')
        : localStorage.getItem('retailEOL');

    const storedObj = JSON.parse(eolData);

    const filteredProcess = Object.keys(storedObj)
      .filter(key => !keysToExclude.includes(key))
      .reduce((obj: any, key) => {
        obj[key] = storedObj[key];
        return obj;
      }, {});

    const total = Object.values(filteredProcess).reduce((c: any, p: any) => c + p, 0);
    if (total != 100) {
      this.sharedService.showEOLTotalErr('End-of-life total must be 100 %', colType);
      return true;
    } else {
      this.route.queryParams.subscribe((values: any) => {
        let value: any = decrypt(values.param ? values.param : '');
        value = value && !value.includes('undefined') ? JSON.parse(value) : '';
        this.scenarioId = value['id'];
      });
      this.sharedService.showEOLTotalErr('');
      var EOLList = JSON.parse(eolData);
      EOLList.eolEntrySource = EOLEntrySource.Manual;
      const payload = {
        EOLListItem: [EOLList],
        packageScenarioId: this.scenarioId,
        storageTypeId: colType == 'isConsumer' ? 3 : 2,
      };

      this.projectsService.updatePackageEOLRetail(payload).subscribe((res: any) => {
        this.updateNotification.next('');
        res.messageType ? this.toaster.error(res.message) : this.toaster.success(res.message);
      });
    }
  }

  getComponentGridData() {
    const colDef: ColDef[] = [];

    colDef.push({
      ...defaultColumn,
      colId: 'MaterialName',
      headerName: 'Material',
      field: 'materialName',
      suppressColumnsToolPanel: true,
      wrapText: true,
      autoHeight: true,
      resizable: false,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      cellRenderer: (params: any) => {
        return (
          `<span class="text-view" title="${params.data.materialName}">` + params.value + `</span>`
        );
      },
    });

    colDef.push({
      ...defaultColumn,
      colId: 'Name',
      headerName: 'Component',
      field: 'name',
      suppressColumnsToolPanel: true,
      wrapText: true,
      autoHeight: true,
      resizable: false,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'Recyclability',
      headerName: 'Recyclability',
      field: 'recyclability',
      suppressColumnsToolPanel: true,
      wrapText: true,
      autoHeight: true,
      resizable: false,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
    });

    return colDef;
  }

  // Reuse data grid column config
  getProjectPreReatilPackEOLDataColumn(): ColDef[] {
    const colDef: ColDef[] = [];

    colDef.push({
      ...defaultColumn,
      colId: 'ComponentName',
      headerName: 'Component',
      field: 'componentName',
      suppressColumnsToolPanel: true,
      cellStyle: { 'text-decoration': 'underline' },
      wrapText: true,
      autoHeight: true,
      resizable: false,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      cellRenderer: (params: any) => {
        const cellValue = params.value;
        const cellElement = document.createElement('span');
        cellElement.style.cursor = 'pointer';
        cellElement.innerHTML = `${cellValue}`;
        cellElement.title = dblclickTooltipDef;
        cellElement.addEventListener('dblclick', () => {
          this.projectsFacade.openAddEditEolPreRetail(
            this.packageScenarioId,
            params.data.id,
            'Review reuse data',
            'preRetail',
            this.StorageTypeEnum.Retail,
            params.data.isConsumer
          );
        });
        return cellElement;
      },
    });

    colDef.push({
      ...defaultColumn,
      colId: 'PercentageOfReuse',
      headerName: 'Reuse (%)',
      field: 'percentageOfReuse',
      suppressColumnsToolPanel: true,
      resizable: false,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      wrapText: true,
      autoHeight: true,
      editable: params =>
        this.accessPermission() &&
        (params.data.percentageOfReuse || (!params.data.noOfTrip && !params.data.percentageOfReuse))
          ? true
          : false,
      cellClass: ['outerGridEdit', 'elipsisFields', 'my-permission-cursor-pointer'],
      cellRenderer: (params: any) => {
        return `<span class="editable-icon editable-icon-padding" title="${params.value}"> ${
          params.value ? params.value : ''
        } ${
          params.data.percentageOfReuse || (!params.data.noOfTrip && !params.data.percentageOfReuse)
            ? '<i class="fa fa-pencil eol-icon-opacity" aria-hidden="true"></i>'
            : ''
        }</span>`;
      },
      cellEditorSelector: (params: any) => {
        if (params.colDef.colId === 'PercentageOfReuse') {
          return {
            component: NumericCellEditor,
          };
        }
        return undefined;
      },
      cellEditorParams: {
        from: 'project-pages-inline',
      },
      valueSetter: (params: any) => {
        const valueChanged = params.data[params.colDef.field] !== params.newValue;
        const oldValue = params.data[params.colDef.field];
        let newVal = params.newValue == 0 ? oldValue : params.newValue;
        if (params.newValue == 0) {
          return this.toaster.error(percentReuseEOLerr);
        }
        params = cloneDeep(params);
        if (valueChanged) {
          newVal = this.validateReuseData(parseFloat(newVal), params.colDef.colId, true);
          if (Array.isArray(newVal)) return;
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number' ? parseFloat(newVal) : newVal;
        }
        const inlineObject = {
          entityId: 'id',
          entityIdValue: params.data['reuseFillingComponentDetailId'],
          entityColumnName: params.colDef.field,
          entityColumnValue: params.data[params.colDef.field],
        };
        if (!this.isEmpty(newVal) && valueChanged) {
          this.inlineEditTrips.next(inlineObject);
          return valueChanged;
        } else return oldValue;
      },
    });

    colDef.push({
      ...defaultColumn,
      colId: 'ReuseType',
      headerName: 'Type',
      field: 'reuseType',
      suppressColumnsToolPanel: true,
      cellClass: 'my-permission-cursor-pointer',
      resizable: false,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      wrapText: true,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'NoOfTrip',
      headerName: 'No of trips',
      field: 'noOfTrip',
      suppressColumnsToolPanel: true,
      wrapText: true,
      autoHeight: true,
      lockPosition: true,
      resizable: false,
      editable: params =>
        this.accessPermission() &&
        (params.data.noOfTrip || (!params.data.noOfTrip && !params.data.percentageOfReuse))
          ? true
          : false,
      cellClass: ['outerGridEdit', 'elipsisFields', 'my-permission-cursor-pointer'],
      cellRenderer: (params: any) => {
        return `<span class="editable-icon editable-icon-padding" title="${params.value}"> ${
          params.value ? params.value : ''
        } ${
          params.data.noOfTrip || (!params.data.noOfTrip && !params.data.percentageOfReuse)
            ? '<i class="fa fa-pencil eol-icon-opacity" aria-hidden="true"></i>'
            : ''
        }</span>`;
      },
      cellEditorSelector: (params: any) => {
        if (params.colDef.colId === 'NoOfTrip') {
          return {
            component: NumericCellEditor,
          };
        }
        return undefined;
      },
      cellEditorParams: {
        from: 'project-pages-inline',
      },
      valueSetter: (params: any) => {
        const valueChanged = params.data[params.colDef.field] !== params.newValue;
        const oldValue = params.data[params.colDef.field];
        let newVal = params.newValue == 0 ? oldValue : params.newValue;
        if (params.newValue == 0) return;
        params = cloneDeep(params);
        if (valueChanged) {
          newVal = this.validateReuseData(parseInt(newVal), params.colDef.colId, true);
          if (Array.isArray(newVal)) return;
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number' ? parseFloat(newVal) : newVal;
        }
        const inlineObject = {
          entityId: 'id',
          entityIdValue: params.data['reuseFillingComponentDetailId'],
          entityColumnName: params.colDef.field,
          entityColumnValue: params.data[params.colDef.field],
        };
        if (!this.isEmpty(newVal) && valueChanged) {
          this.inlineEditTrips.next(inlineObject);
          return valueChanged;
        } else return oldValue;
      },
    });

    colDef.push({
      ...defaultColumn,
      colId: 'Transport',
      headerName: 'Transport',
      field: 'transport',
      suppressColumnsToolPanel: true,
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'iconsOfTransport',
      lockPosition: true,
      resizable: false,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      wrapText: true,
      sortable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'ProcessName',
      headerName: 'Process',
      field: 'processName',
      suppressColumnsToolPanel: true,
      cellClass: 'my-permission-cursor-pointer',
      wrapText: false,
      autoHeight: true,
      lockPosition: true,
      resizable: false,
      cellRenderer: (params: any) => {
        const cellDiv = document.createElement('div');
        cellDiv.style.display = 'grid';
        cellDiv.style.gridTemplateColumns = '1fr';
        params.data?.reuseProcesAmountList?.forEach((data: any) => {
          const cellValue = data.processName;
          const cellElement = document.createElement('span');

          cellElement.innerHTML = `${cellValue}`;
          cellElement.style.whiteSpace = 'nowrap';
          cellElement.style.overflow = 'hidden';
          cellElement.style.textOverflow = 'ellipsis';
          cellElement.style.display = 'block';
          cellElement.title = cellValue;

          cellDiv.appendChild(cellElement);
          cellDiv.appendChild(document.createElement('br'));
        });
        return cellDiv;
      },
    });

    colDef.push({
      ...defaultColumn,
      colId: 'ReuseProcesAmount',
      headerName: 'Amount',
      field: 'reuseProcesAmount',
      suppressColumnsToolPanel: true,
      cellClass: 'my-permission-cursor-pointer',
      wrapText: false,
      autoHeight: true,
      lockVisible: true,
      resizable: false,
      editable: false,
      cellRenderer: (params: any) => {
        const cellDiv = document.createElement('div');
        cellDiv.style.display = 'grid';
        cellDiv.style.gridTemplateColumns = '1fr';
        params.data?.reuseProcesAmountList?.forEach((data: any) => {
          const cellValue = data.processAmount;
          const cellElement = document.createElement('span');

          cellElement.innerHTML = `${cellValue}`;
          cellElement.style.whiteSpace = 'nowrap';
          cellElement.style.overflow = 'hidden';
          cellElement.style.textOverflow = 'ellipsis';
          cellElement.style.display = 'block';
          cellElement.title = cellValue;

          cellDiv.appendChild(cellElement);
          cellDiv.appendChild(document.createElement('br'));
        });
        return cellDiv;
      },
    });

    this.accessPermission() &&
      colDef.push({
        ...defaultColumn,
        colId: 'Actions',
        headerName: 'Actions',
        field: 'Actions',
        sortable: false,
        maxWidth: grid_widths_map.sm_column_xs,
        suppressColumnsToolPanel: true,
        cellClass: 'my-permission-cursor-pointer',
        cellRenderer: 'actionRenderer',
        cellRendererParams: {
          showEdit: true,
          showDelete: true,
          deleteData: (data: any) => {
            this.deleteConfirmation(data, 'preRetail');
          },
          editData: (data: any) => {
            this.projectsFacade.openAddEditEolPreRetail(
              this.packageScenarioId,
              data.id,
              'Review reuse data',
              'preRetail',
              data.isConsumer ? this.StorageTypeEnum.Consumer : this.StorageTypeEnum.Retail,
              data.isConsumer
            );
          },
        },
        lockVisible: true,
        lockPosition: right,
        resizable: false,
      });
    return colDef;
  }

  getProjectPostReatilPackEOLDataColumn(): ColDef[] {
    const colDef: ColDef[] = [];

    colDef.push({
      ...defaultColumn,
      colId: 'ComponentName',
      headerName: 'Component',
      field: 'componentName',
      suppressColumnsToolPanel: true,
      cellStyle: { 'text-decoration': 'underline' },
      wrapText: true,
      autoHeight: true,
      resizable: false,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      cellRenderer: (params: any) => {
        const cellValue = params.value;
        const cellElement = document.createElement('span');
        cellElement.style.cursor = 'pointer';
        cellElement.innerHTML = `${cellValue}`;
        cellElement.title = dblclickTooltipDef;
        cellElement.addEventListener('dblclick', () => {
          this.projectsFacade.openAddEditEolPreRetail(
            this.packageScenarioId,
            params.data.id,
            'Review reuse data',
            'postRetail',
            this.StorageTypeEnum.Retail
          );
        });
        return cellElement;
      },
    });

    colDef.push({
      ...defaultColumn,
      colId: 'PercentageOfReuse',
      headerName: 'Reuse (%)',
      field: 'percentageOfReuse',
      suppressColumnsToolPanel: true,
      cellClass: 'my-permission-cursor-pointer',
      resizable: false,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      wrapText: true,
    });
    colDef.push({
      ...defaultColumn,
      colId: 'NoOfTrip',
      headerName: 'No of trips',
      field: 'noOfTrip',
      suppressColumnsToolPanel: true,
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockPosition: true,
      resizable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'Transport',
      headerName: 'Transport',
      field: 'transport',
      suppressColumnsToolPanel: true,
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'iconsOfTransport',
      lockPosition: true,
      resizable: false,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      wrapText: true,
      sortable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'ProcessName',
      headerName: 'Process',
      field: 'processName',
      suppressColumnsToolPanel: true,
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockPosition: true,
      resizable: false,
    });
    colDef.push({
      ...defaultColumn,
      colId: 'ReuseProcesAmount',
      headerName: 'Amount',
      field: 'reuseProcesAmount',
      suppressColumnsToolPanel: true,
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockPosition: true,
      resizable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'Actions',
      headerName: 'Actions',
      field: 'Actions',
      sortable: false,
      maxWidth: grid_widths_map.sm_column_xs,
      suppressColumnsToolPanel: true,
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'actionRenderer',
      cellRendererParams: {
        showEdit: true,
        showDelete: true,
        deleteData: (data: any) => {
          this.deleteConfirmation(data, 'postRetail');
        },
        editData: (data: any, pathName: any) => {
          this.projectsFacade.openAddEditEolPreRetail(
            this.packageScenarioId,
            data.id,
            'Review reuse data',
            'postRetail',
            this.StorageTypeEnum.Retail
          );
        },
      },
      lockVisible: true,
      lockPosition: right,
      resizable: false,
    });
    return colDef;
  }

  // transport retail manual location
  getTransportModalForManualLocationDataColumn(): ColDef[] {
    const colDef: ColDef[] = [];
    colDef.push({
      ...defaultColumn,
      colId: 'OriginName',
      headerName: 'Location',
      field: 'originName',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
    });
    colDef.push({
      ...defaultColumn,
      colId: 'TransportModeName',
      headerName: 'Transport mode',
      field: 'transportModeName',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
    });
    colDef.push({
      ...defaultColumn,
      colId: 'PersanteageMassLoad',
      headerName: 'Load efficiency',
      field: 'persanteageMassLoad',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
      hide: true,
    });
    colDef.push({
      ...defaultColumn,
      colId: 'Distance',
      headerName: DISTANCE_KM_LABEL,
      field: 'distance',
      cellClass: 'my-permission-cursor-pointer',
      cellEditorSelector: (params: any) => {
        if (params.colDef.colId === 'Distance') {
          return {
            component: NumericCellEditor,
          };
        }
        return undefined;
      },
      cellEditorParams: {
        isCheckTransportDistance: true,
      },
      cellStyle: params => {
        const isCellEditable = params.rowIndex == 0 ? false : true;
        return {
          'pointer-events': isCellEditable ? '' : 'none',
        };
      },
      valueSetter: (params: any) => {
        const newVal = params.newValue;
        const valueChanged = params.data[params.colDef.field] !== newVal;
        const oldValue = params.data[params.colDef.field];
        params = cloneDeep(params);
        if (valueChanged) {
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number' ? parseFloat(newVal) : newVal;
        }
        if (!this.isEmpty(newVal) && valueChanged) {
          this.projectsFacade.manudaLocationPreRetail(params.data, 'edit');
          this.editProjectPreRetailSub.next([params.data, false]);
          return valueChanged;
        } else {
          return oldValue;
        }
      },
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: true,
      sortable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'actions',
      headerName: 'Actions',
      field: 'actions',
      sortable: false,
      maxWidth: grid_widths_map.sm_column_xs,
      suppressColumnsToolPanel: true,
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'inlineActionButtonComponent',
      cellRendererParams: {
        showInlineEdit: true,
        showInlineDelete: true,
        from: 'projectPages',
        deleteInlineGrid: (data: any) => {
          this.alertConfirmationForManudalLocation(data);
        },
        editDataInline: (data: any, pathName: any) => {
          this.projectsFacade.manudaLocationPreRetail(data, 'edit');
          this.editProjectPreRetailSub.next([data, true]);
        },
        saveDataInline: (data: any) => {
          this.saveProjectManualDistanceEOL.next([data, 'manualLocation']);
        },
        clearInputField: (data: any) => {
          this.clearInputFieldProjectManualDistanceEOL.next(data);
        },
      },
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      resizable: false,
    });
    return colDef;
  }

  getTransportModalForManualDestinationDataColumn(): ColDef[] {
    const colDef: ColDef[] = [];

    colDef.push({
      ...defaultColumn,
      colId: 'TransportModeName',
      headerName: 'Transport mode',
      field: 'transportModeName',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
    });
    colDef.push({
      ...defaultColumn,
      colId: 'PersanteageMassLoad',
      headerName: 'Load efficiency',
      field: 'persanteageMassLoad',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
      hide: true,
    });
    colDef.push({
      ...defaultColumn,
      colId: 'Distance',
      headerName: DISTANCE_KM_LABEL,
      field: 'distance',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'actions',
      headerName: 'Actions',
      field: 'actions',
      sortable: false,
      maxWidth: grid_widths_map.sm_column_xs,
      suppressColumnsToolPanel: true,
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'inlineActionButtonComponent',
      cellRendererParams: {
        showInlineEdit: true,
        showInlineDelete: true,
        from: 'projectPages',
        deleteInlineGrid: (data: any) => {
          this.alertConfirmationForDistance(data);
        },
        editDataInline: (data: any, pathName: any) => {
          this.projectsFacade.manudaDistancePreRetail(data, 'edit');
          this.editProjectPreRetailManualDistanceSub.next(data);
        },
        saveDataInline: (data: any) => {
          this.saveProjectManualDistanceEOL.next([data, 'manualDistance']);
        },
        clearInputField: (data: any) => {
          this.clearInputFieldProjectManualDistanceEOL.next(data);
        },
      },
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      resizable: false,
    });
    return colDef;
  }

  getCustomTransportItemsList(): ColDef[] {
    const colDef: ColDef[] = [];

    colDef.push({
      ...defaultColumn,
      colId: 'CityName',
      headerName: 'Origin-destination',
      field: 'cityName',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'TransportModeName',
      headerName: 'Transport mode',
      field: 'transportModeName',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
    });
    colDef.push({
      ...defaultColumn,
      colId: 'Distance',
      headerName: DISTANCE_KM_LABEL,
      field: 'distance',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
    });

    return colDef;
  }

  // Data grid for popup
  newPreRetailTransportColumns(): ColDef[] {
    const colDef: ColDef[] = [];
    colDef.push({
      ...defaultColumn,
      colId: 'ComponentName',
      headerName: 'Component',
      field: 'componentName',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
    });
    colDef.push({
      ...defaultColumn,
      colId: 'percentageOfReuse',
      headerName: 'Reuse (%)',
      field: 'percentageOfReuse',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: params =>
        this.accessPermission() &&
        (params.data.percentageOfReuse || (!params.data.noOfTrip && !params.data.percentageOfReuse))
          ? true
          : false,
      sortable: false,
      cellClass: ['outerGridEdit', 'elipsisFields', 'my-permission-cursor-pointer'],
      cellRenderer: (params: any) => {
        return `<span class="editable-icon editable-icon-padding" title="${params.value}"> ${
          params.value ? params.value : ''
        } ${
          params.data.percentageOfReuse || (!params.data.noOfTrip && !params.data.percentageOfReuse)
            ? '<i class="fa fa-pencil eol-icon-opacity" aria-hidden="true"></i>'
            : ''
        }</span>`;
      },
      valueSetter: (params: any) => {
        const valueChanged = params.data[params.colDef.field] !== params.newValue;
        const oldValue = params.data[params.colDef.field];
        let newVal = params.newValue == 0 ? oldValue : params.newValue;
        if (params.newValue == 0) return;
        params = cloneDeep(params);
        if (valueChanged) {
          newVal = this.validateReuseData(parseInt(newVal), params.colDef.colId);
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number' ? parseFloat(newVal) : newVal;
        }
        if (!this.isEmpty(newVal) && valueChanged) {
          this.projectsFacade.eolPreRetailAddComponent(
            params.data,
            'inlineEdit',
            true,
            params.node.rowIndex
          );
          return valueChanged;
        } else return oldValue;
      },
    });
    colDef.push({
      ...defaultColumn,
      colId: 'NoOfTrip',
      headerName: 'No of trips',
      field: 'noOfTrip',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: params =>
        this.accessPermission() &&
        (params.data.noOfTrip || (!params.data.noOfTrip && !params.data.percentageOfReuse))
          ? true
          : false,
      sortable: false,
      cellClass: ['outerGridEdit', 'elipsisFields', 'my-permission-cursor-pointer'],
      cellRenderer: (params: any) => {
        return `<span class="editable-icon editable-icon-padding" title="${params.value}"> ${
          params.value ? params.value : ''
        } ${
          params.data.noOfTrip || (!params.data.noOfTrip && !params.data.percentageOfReuse)
            ? '<i class="fa fa-pencil eol-icon-opacity" aria-hidden="true"></i>'
            : ''
        }</span>`;
      },
      valueSetter: (params: any) => {
        const valueChanged = params.data[params.colDef.field] !== params.newValue;
        const oldValue = params.data[params.colDef.field];
        let newVal = params.newValue == 0 ? oldValue : params.newValue;
        if (params.newValue == 0) return;
        params = cloneDeep(params);
        if (valueChanged) {
          newVal = this.validateReuseData(parseInt(newVal), params.colDef.colId);
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number' ? parseFloat(newVal) : newVal;
        }
        if (!this.isEmpty(newVal) && valueChanged) {
          this.projectsFacade.eolPreRetailAddComponent(
            params.data,
            'inlineEdit',
            true,
            params.node.rowIndex
          );
          return valueChanged;
        } else return oldValue;
      },
    });

    colDef.push({
      ...defaultColumn,
      colId: 'Transport',
      headerName: 'Transport',
      field: 'transport',
      cellRenderer: 'iconsOfTransport',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
    });
    colDef.push({
      ...defaultColumn,
      colId: 'ProcessName',
      headerName: 'Process',
      field: 'processName',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
      cellRenderer: (params: any) => {
        const cellDiv = document.createElement('div');
        cellDiv.style.display = 'grid';
        cellDiv.style.gridTemplateColumns = '1fr';
        params.data?.reuseProcesAmountList?.forEach((data: any) => {
          const cellValue = data.processName;
          const cellElement = document.createElement('span');

          cellElement.innerHTML = `${cellValue}`;
          cellElement.style.whiteSpace = 'nowrap';
          cellElement.style.overflow = 'hidden';
          cellElement.style.textOverflow = 'ellipsis';
          cellElement.style.display = 'block';
          cellElement.title = cellValue;

          cellDiv.appendChild(cellElement);
          cellDiv.appendChild(document.createElement('br'));
        });
        return cellDiv;
      },
    });
    colDef.push({
      ...defaultColumn,
      colId: 'ReuseProcesAmount',
      headerName: 'Amount',
      field: 'reuseProcesAmount',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
      cellRenderer: (params: any) => {
        const cellDiv = document.createElement('div');
        cellDiv.style.display = 'grid';
        cellDiv.style.gridTemplateColumns = '1fr';
        params.data?.reuseProcesAmountList?.forEach((data: any) => {
          const cellValue = data.processAmount || data.amount;
          const cellElement = document.createElement('span');

          cellElement.innerHTML = `${cellValue}`;
          cellElement.style.whiteSpace = 'nowrap';
          cellElement.style.overflow = 'hidden';
          cellElement.style.textOverflow = 'ellipsis';
          cellElement.style.display = 'block';
          cellElement.title = cellValue;

          cellDiv.appendChild(cellElement);
          cellDiv.appendChild(document.createElement('br'));
        });
        return cellDiv;
      },
    });

    colDef.push({
      ...defaultColumn,
      colId: 'actions',
      headerName: 'Actions',
      field: 'actions',
      sortable: false,
      maxWidth: grid_widths_map.sm_column_xs,
      cellRenderer: 'actionRenderer',
      cellRendererParams: {
        showDelete: true,
        showEdit: true,
        deleteData: (data: any) => {
          this.alertConfirmationForPreRetailComponent(data);
        },
        editData: (data: any) => {
          this.reuseComponentsItem.next(data);
        },
      },
      lockVisible: true,
      autoHeaderHeight: true,
      lockPosition: right,
      resizable: false,
    });
    return colDef;
  }

  reuseColumns(): ColDef[] {
    const colDef: ColDef[] = [];
    let enableEditing: any;
    let resetData: any;
    let newReuseProcessname: any;
    let newReuseProcessId: any;
    colDef.push({
      ...defaultColumn,
      colId: 'ProcessName',
      headerName: 'Reuse filling process name',
      field: 'processName',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: true,
      sortable: false,
      cellEditor: 'GridCellRendererComponent',
      cellEditorParams: {
        from: 'reuseProcessName',
        editData: (data: any) => {
          newReuseProcessname = data.name;
          newReuseProcessId = data.id;
        },
      },
      cellRenderer: (params: any) => {
        return `<div class="truncated-text-input-quality" title="${params.data.processName}">${
          params.data.processName ? params.data.processName : ''
        }</div>`;
      },
      valueSetter: (params: any) => {
        const newVal = !!newReuseProcessname ? newReuseProcessname : 'Select process';
        const valueChanged =
          !!newReuseProcessname && !resetData ? params.data[params.colDef.field] !== newVal : false;
        params.data = { ...params.data };
        if (valueChanged) {
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number' ? parseFloat(newVal) : newVal;
          if (params.data?.isCustomReuse) {
            params.data['customReuseId'] = newReuseProcessId;
          } else {
            params.data['energyProcessId'] = newReuseProcessId;
          }
          params.data['enableEditing'] = enableEditing;
          const colId = params.colDef.field;
          this.projectsFacade.checkUpdateActionForReuse$
            .pipe(take(1))
            .subscribe((flag: boolean) => {
              flag && this.projectsFacade.preRetailReuseAction(params.data, 'edit', colId);
            });
          newReuseProcessname = '';
          newReuseProcessId = '';
        }
        return valueChanged;
      },
      valueGetter: params => {
        return params.data.processName;
      },
    });
    colDef.push({
      ...defaultColumn,
      colId: 'Amount',
      headerName: 'Amount',
      field: 'amount',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
      cellEditorSelector: (params: any) => {
        if (params.colDef.colId === 'Amount') {
          return {
            component: NumericCellEditor,
          };
        }
        return undefined;
      },
      cellEditorParams: {
        from: 'ingredient-weight',
      },
      valueSetter: (params: any) => {
        const newVal = params.newValue;
        const valueChanged = !resetData && params.data[params.colDef.field] !== newVal;
        params.data = { ...params.data };
        if (valueChanged) {
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number' ? parseFloat(newVal) : newVal;
          params.data['enableEditing'] = enableEditing;
        }
        const colId = params.colDef.colId;
        this.projectsFacade.checkUpdateActionForReuse$.pipe(take(1)).subscribe((flag: boolean) => {
          flag && this.projectsFacade.preRetailReuseAction(params.data, 'edit', colId);
        });
        return false;
      },
    });
    colDef.push({
      ...defaultColumn,
      colId: 'CostperUnit',
      headerName: 'Cost per unit',
      field: 'costperUnit',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: true,
      sortable: false,
      cellEditorSelector: (params: any) => {
        if (params.colDef.colId === 'CostperUnit') {
          return {
            component: NumericCellEditor,
          };
        }
        return undefined;
      },
      cellEditorParams: {
        from: 'ingredient-weight',
      },
      valueSetter: (params: any) => {
        const newVal = params.newValue;
        const valueChanged = !resetData && params.data[params.colDef.field] !== newVal;
        params.data = { ...params.data };
        if (valueChanged) {
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number' ? parseFloat(newVal) : newVal;
          params.data['enableEditing'] = enableEditing;
        }
        const colId = params.colDef.colId;
        this.projectsFacade.checkUpdateActionForReuse$.pipe(take(1)).subscribe((flag: boolean) => {
          flag && this.projectsFacade.preRetailReuseAction(params.data, 'edit', colId);
        });
        return false;
      },
    });

    colDef.push({
      ...defaultColumn,
      colId: 'CostperPackage',
      headerName: 'Cost per package',
      field: 'costperPackage',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: true,
      sortable: false,
      cellEditorSelector: (params: any) => {
        if (params.colDef.colId === 'CostperPackage') {
          return {
            component: NumericCellEditor,
          };
        }
        return undefined;
      },
      cellEditorParams: {
        from: 'ingredient-weight',
      },
      valueSetter: (params: any) => {
        const newVal = params.newValue;
        const valueChanged = !resetData && params.data[params.colDef.field] !== newVal;
        params.data = { ...params.data };
        if (valueChanged) {
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number' ? parseFloat(newVal) : newVal;
          params.data['enableEditing'] = enableEditing;
        }
        const colId = params.colDef.colId;
        this.projectsFacade.checkUpdateActionForReuse$.pipe(take(1)).subscribe((flag: boolean) => {
          flag && this.projectsFacade.preRetailReuseAction(params.data, 'edit', colId);
        });
        return false;
      },
    });

    colDef.push({
      ...defaultColumn,
      colId: 'actions',
      headerName: 'Actions',
      field: 'actions',
      sortable: false,
      maxWidth: grid_widths_map.sm_column_xs,
      cellRenderer: 'inlineActionButtonComponent',
      cellRendererParams: {
        showInlineEdit: true,
        showInlineDelete: true,
        from: 'reuseProcessItem',
        inlineEditData: (data: any) => {
          enableEditing = data.data.enableEditing;
          resetData = data.data.resetData;
        },
        deleteInlineGrid: (data: any) => {
          this.projectsFacade.preRetailReuseAction(data, 'delete');
        },
      },
      lockVisible: true,
      autoHeaderHeight: true,
      lockPosition: right,
      resizable: false,
    });
    return colDef;
  }

  // Data grid for popup (WHEN COST IS ENABLED)
  newPreRetailTransportColumnsCost(): ColDef[] {
    const colDef: ColDef[] = [];
    colDef.push({
      ...defaultColumn,
      colId: 'ComponentName',
      headerName: 'Component',
      field: 'componentName',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
    });
    colDef.push({
      ...defaultColumn,
      colId: 'percentageOfReuse',
      headerName: 'Reuse (%)',
      field: 'percentageOfReuse',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: params =>
        this.accessPermission() &&
        (params.data.percentageOfReuse || (!params.data.noOfTrip && !params.data.percentageOfReuse))
          ? true
          : false,
      sortable: false,
      cellClass: ['outerGridEdit', 'elipsisFields', 'my-permission-cursor-pointer'],
      cellRenderer: (params: any) => {
        return `<span class="editable-icon editable-icon-padding" title="${params.value}"> ${
          params.value ? params.value : ''
        } ${
          params.data.percentageOfReuse || (!params.data.noOfTrip && !params.data.percentageOfReuse)
            ? '<i class="fa fa-pencil eol-icon-opacity" aria-hidden="true"></i>'
            : ''
        }</span>`;
      },
      valueSetter: (params: any) => {
        const valueChanged = params.data[params.colDef.field] !== params.newValue;
        const oldValue = params.data[params.colDef.field];
        let newVal = params.newValue == 0 ? oldValue : params.newValue;
        if (params.newValue == 0) return;
        params = cloneDeep(params);
        if (valueChanged) {
          newVal = this.validateReuseData(parseInt(newVal), params.colDef.colId);
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number' ? parseFloat(newVal) : newVal;
        }
        if (!this.isEmpty(newVal) && valueChanged) {
          this.projectsFacade.eolPreRetailAddComponent(
            params.data,
            'inlineEdit',
            true,
            params.node.rowIndex
          );
          return valueChanged;
        } else return oldValue;
      },
    });
    colDef.push({
      ...defaultColumn,
      colId: 'NoOfTrip',
      headerName: 'No of trips',
      field: 'noOfTrip',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: params =>
        this.accessPermission() &&
        (params.data.noOfTrip || (!params.data.noOfTrip && !params.data.percentageOfReuse))
          ? true
          : false,
      sortable: false,
      cellClass: ['outerGridEdit', 'elipsisFields', 'my-permission-cursor-pointer'],
      cellRenderer: (params: any) => {
        return `<span class="editable-icon editable-icon-padding" title="${params.value}"> ${
          params.value ? params.value : ''
        } ${
          params.data.noOfTrip || (!params.data.noOfTrip && !params.data.percentageOfReuse)
            ? '<i class="fa fa-pencil eol-icon-opacity" aria-hidden="true"></i>'
            : ''
        }</span>`;
      },
      valueSetter: (params: any) => {
        const valueChanged = params.data[params.colDef.field] !== params.newValue;
        const oldValue = params.data[params.colDef.field];
        let newVal = params.newValue == 0 ? oldValue : params.newValue;
        if (params.newValue == 0) return;
        params = cloneDeep(params);
        if (valueChanged) {
          newVal = this.validateReuseData(parseInt(newVal), params.colDef.colId);
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number' ? parseFloat(newVal) : newVal;
        }
        if (!this.isEmpty(newVal) && valueChanged) {
          this.projectsFacade.eolPreRetailAddComponent(
            params.data,
            'inlineEdit',
            true,
            params.node.rowIndex
          );
          return valueChanged;
        } else return oldValue;
      },
    });

    colDef.push({
      ...defaultColumn,
      colId: 'Transport',
      headerName: 'Transport',
      field: 'transport',
      cellRenderer: 'iconsOfTransport',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
    });
    colDef.push({
      ...defaultColumn,
      colId: 'ProcessName',
      headerName: 'Process',
      field: 'processName',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
      cellRenderer: (params: any) => {
        const cellDiv = document.createElement('div');
        cellDiv.style.display = 'grid';
        cellDiv.style.gridTemplateColumns = '1fr';
        params.data?.reuseProcesAmountList?.forEach((data: any) => {
          const cellValue = data.processName;
          const cellElement = document.createElement('span');

          cellElement.innerHTML = `${cellValue}`;
          cellElement.style.whiteSpace = 'nowrap';
          cellElement.style.overflow = 'hidden';
          cellElement.style.textOverflow = 'ellipsis';
          cellElement.style.display = 'block';
          cellElement.title = cellValue;

          cellDiv.appendChild(cellElement);
          cellDiv.appendChild(document.createElement('br'));
        });
        return cellDiv;
      },
    });
    colDef.push({
      ...defaultColumn,
      colId: 'ReuseProcesAmount',
      headerName: 'Amount',
      field: 'reuseProcesAmount',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
      cellRenderer: (params: any) => {
        const cellDiv = document.createElement('div');
        cellDiv.style.display = 'grid';
        cellDiv.style.gridTemplateColumns = '1fr';
        params.data?.reuseProcesAmountList?.forEach((data: any) => {
          const cellValue = data.processAmount || data.amount;
          const cellElement = document.createElement('span');

          cellElement.innerHTML = `${cellValue}`;
          cellElement.style.whiteSpace = 'nowrap';
          cellElement.style.overflow = 'hidden';
          cellElement.style.textOverflow = 'ellipsis';
          cellElement.style.display = 'block';
          cellElement.title = cellValue;

          cellDiv.appendChild(cellElement);
          cellDiv.appendChild(document.createElement('br'));
        });
        return cellDiv;
      },
    });
    colDef.push({
      ...defaultColumn,
      colId: 'ReuseProcesCostperUnit',
      headerName: 'Cost in USD',
      field: 'reuseProcesCostperUnit',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
      cellRenderer: (params: any) => {
        const cellDiv = document.createElement('div');
        cellDiv.style.display = 'grid';
        cellDiv.style.gridTemplateColumns = '1fr';
        params.data?.reuseProcesAmountList?.forEach((data: any) => {
          const cellValue = data.processCost || data.costperUnit;
          const cellElement = document.createElement('span');

          cellElement.innerHTML = `${cellValue}`;
          cellElement.style.whiteSpace = 'nowrap';
          cellElement.style.overflow = 'hidden';
          cellElement.style.textOverflow = 'ellipsis';
          cellElement.style.display = 'block';
          cellElement.title = cellValue;

          cellDiv.appendChild(cellElement);
          cellDiv.appendChild(document.createElement('br'));
        });
        return cellDiv;
      },
    });
    colDef.push({
      ...defaultColumn,
      colId: 'ReuseProcesCostperPackage',
      headerName: 'Cost per package',
      field: 'reuseProcesCostperPackage',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
      cellRenderer: (params: any) => {
        const cellDiv = document.createElement('div');
        cellDiv.style.display = 'grid';
        cellDiv.style.gridTemplateColumns = '1fr';
        params.data?.reuseProcesAmountList?.forEach((data: any) => {
          const cellValue = data.processCostPerPackage || data.costperPackage;
          const cellElement = document.createElement('span');

          cellElement.innerHTML = `${cellValue}`;
          cellElement.style.whiteSpace = 'nowrap';
          cellElement.style.overflow = 'hidden';
          cellElement.style.textOverflow = 'ellipsis';
          cellElement.style.display = 'block';
          cellElement.title = cellValue;

          cellDiv.appendChild(cellElement);
          cellDiv.appendChild(document.createElement('br'));
        });
        return cellDiv;
      },
    });
    colDef.push({
      ...defaultColumn,
      colId: 'actions',
      headerName: 'Actions',
      field: 'actions',
      sortable: false,
      maxWidth: grid_widths_map.sm_column_xs,
      cellRenderer: 'actionRenderer',
      cellRendererParams: {
        showDelete: true,
        showEdit: true,
        deleteData: (data: any) => {
          this.alertConfirmationForPreRetailComponent(data);
        },
        editData: (data: any) => {
          this.reuseComponentsItem.next(data);
        },
      },
      lockVisible: true,
      autoHeaderHeight: true,
      lockPosition: right,
      resizable: false,
    });
    return colDef;
  }

  accessPermission(): boolean {
    const access = this.accessService.hasPermission('edit', '', 'packaging');
    return access;
  }

  alertConfirmationForManudalLocation(data: any) {
    this.swalService.confirm('Are you sure?', DELETE_RECORD_LABEL).then(confirmed => {
      if (confirmed) {
        this.projectsFacade.getTransportScenarioManudalLocationList$.subscribe((records: any) => {
          this.allTransportLocationRecords = records;
        });
        this.projectsFacade.manudaLocationPreRetail(data, 'delete');
        this.checkDeleteRowPreRetail.next([this.allTransportLocationRecords, data, true]);
      }
    });
  }

  alertConfirmationForDistance(data: any) {
    this.swalService.confirm('Are you sure?', DELETE_RECORD_LABEL).then(confirmed => {
      if (confirmed) {
        this.projectsFacade.manudaDistancePreRetail(data, 'delete');
        this.checkDeleteRowPreRetail.next([undefined, data, false]);
      }
    });
  }

  alertConfirmationForPreRetailComponent(data: any) {
    this.swalService.confirm('Are you sure?', DELETE_RECORD_LABEL).then(confirmed => {
      if (confirmed) {
        this.reuseDeleteComponentsItem.next('');
        this.projectsFacade.eolPreRetailAddComponent(data, 'delete');
      }
    });
  }

  getPreRetail() {
    this.editPreRetailSuccess.next(() => {});
  }

  getPostRetail() {
    this.editPostRetailSuccess.next(() => {});
  }

  deleteConfirmation(data: any, path: any) {
    this.swalService.confirm('Are you sure?', DELETE_RECORD_LABEL).then(confirmed => {
      if (confirmed) {
        this.deletePackageEOL.next([data, path]);
      }
    });
  }

  isEmpty(val: any) {
    if (val !== undefined || val !== null || val !== '') {
      val = val.trim();
    }
    return val === undefined || val === null || val === '';
  }

  validateReuseData(value: number, colId: string, isMainGrid?: boolean): string | string[] {
    let maxValue: number;
    let minValue: number;
    let errorMsg: string;

    switch (colId) {
      case 'NoOfTrip':
        maxValue = 9999;
        minValue = 2;
        break;

      default:
        maxValue = 99.99;
        minValue = 0.1;
        break;
    }

    errorMsg =
      value > maxValue
        ? ((value = maxValue), colId === 'NoOfTrip' ? noOfTripsErr : percentReuseEOLerr)
        : value < minValue
        ? ((value = minValue), colId === 'NoOfTrip' ? minNoOfTripsErr : minPercentReuseEOLerr)
        : '';

    if (errorMsg && isMainGrid) {
      this.toaster.error(errorMsg);
      return [value.toString(), errorMsg];
    } else {
      this.projectsFacade.updateComponentErrStr(errorMsg);
      return value.toString();
    }
  }
}
